// Where a person has to be today — the line under their name on the Today
// tab (Oct 23, 2026). Greg: "list if they have a build or adjuster meeting
// that day. that is where the team member will be working. maybe if james has
// a button to click to create assigned area for the day around build or
// adjuster meeting and it pulls up canvas map at that location."
//
// One chip per stop (build / adjuster meeting / reinspection, from
// canvas_day_stops) and next to it either "Area here" — opens the canvas map
// in draw mode landed on the stop with this person picked and the area named
// after the street — or the area already drawn around that stop THAT THIS
// PERSON IS ON, for this day. When only a teammate (the split partner) has an
// area around the stop, the row offers to add this person to it, and keeps
// "Area here" so James can still draw them their own. No stop: says so, with
// a plain Draw link for a crew manager.

import { useState } from "react";
import { addAreaMember, areaMapHref, areaName, drawAreaHref, firstName, type AreaStats } from "../../../lib/canvasAreas";
import {
  areaDayState, areaForStop, areaNameFromStop, hasCoords, partnerId, stopAddress, stopTitle, stopsFor,
  type DayStop,
} from "../../../lib/dailyAreas";

interface Person { id: string; full_name: string; role: string }

const KIND_ICON: Record<DayStop["kind"], string> = { build: "🏠", adjuster: "📋", reinspection: "🔁" };

export default function PersonStops({ person, stops, areas, day, canWrite, people, onChanged }: {
  person: Person;
  /** Every stop for the day the board has (filtered to this person here); null while loading. */
  stops: DayStop[] | null;
  areas: AreaStats[] | null;
  day: string;
  canWrite: boolean;
  people: Person[];
  /** Refetch the areas after this person was added to a teammate's area. */
  onChanged?: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  if (stops === null) return null;
  const mine = stopsFor(stops, person.id);
  const first = firstName(person.full_name);
  // The partner on a split deal, by first name — nothing when they're not on the board (off today, or not a canvasable role).
  const nameOf = (id: string | null) => {
    const full = id ? people.find((p) => p.id === id)?.full_name : null;
    return full ? firstName(full) : null;
  };
  const todays = (areas ?? []).filter((a) => areaDayState(a, day) === "today" && a.reps.some((r) => r.user_id === person.id));
  const unanchored = todays.filter((a) => !mine.some((s) => s.deal_id === a.anchor_deal_id));

  async function join(a: AreaStats) {
    setBusy(a.area_group_id); setMsg(null);
    try {
      const r = await addAreaMember(a.area_group_id, person.id);
      setMsg(r.already ? `${first} was already on ${areaName(a)}.` : `${first} added to ${areaName(a)}.`);
      onChanged?.();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  if (mine.length === 0) {
    return (
      <div className="flex w-full flex-wrap items-center gap-2 pb-2.5 pl-3 pr-2 text-[11px] text-slate-500">
        <span>No build or adjuster meeting {dayWord(day)}.</span>
        {unanchored.map((a) => <AreaSet key={a.area_group_id} area={a} />)}
        {canWrite && unanchored.length === 0 && (
          <a href={drawAreaHref(person.id, { forDate: day })} target="_blank" rel="noopener"
            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50">
            ✏️ Draw {dayWord(day) === "today" ? "today's" : "an"} area ↗
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-wrap items-center gap-2 pb-2.5 pl-3 pr-2">
      {mine.map((s) => {
        const partner = nameOf(partnerId(s, person.id));
        const set = areaForStop(areas ?? [], s, person.id, day);
        const partnersArea = set ? undefined : areaForStop(areas ?? [], s, undefined, day);
        const canDraw = canWrite && hasCoords(s);
        return (
          <span key={`${s.deal_id}-${s.kind}`} className="inline-flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-slate-800"
              title={stopAddress(s)}>
              <span aria-hidden="true">{KIND_ICON[s.kind]}</span>
              <span>{stopTitle(s)}</span>
              <span className="font-normal text-slate-500">· {stopAddress(s)}</span>
              {partner && <span className="font-normal text-slate-500">· with {partner}</span>}
            </span>
            {set ? (
              <AreaSet area={set} />
            ) : (
              <>
                {partnersArea && canWrite && (
                  <button type="button" onClick={() => void join(partnersArea)} disabled={busy !== null}
                    className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                    title={`${partnersArea.reps.map((r) => r.full_name).join(", ")} already have an area around this stop — put ${first} on it too`}>
                    {busy === partnersArea.area_group_id ? "Adding…" : `＋ Add ${first} to ${partnersArea.reps.map((r) => firstName(r.full_name)).join(" & ")}'s area`}
                  </button>
                )}
                {partnersArea && !canWrite && <AreaSet area={partnersArea} label={`${partnersArea.reps.map((r) => firstName(r.full_name)).join(" & ")}'s area`} />}
                {canDraw ? (
                  <a href={drawAreaHref(person.id, {
                      lat: s.lat, lon: s.lon, zoom: 16, anchorDealId: s.deal_id, anchorKind: s.kind,
                      forDate: day, label: areaNameFromStop(s),
                    })}
                    target="_blank" rel="noopener"
                    className="rounded-md border border-orange-400 bg-white px-2 py-1 text-[11px] font-bold text-orange-700 hover:bg-orange-50"
                    title={`Draw ${first}'s area for ${dayWord(day)} around ${stopAddress(s)} on the canvas map`}>
                    ✏️ Area here ↗
                  </a>
                ) : canWrite ? (
                  <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-400"
                    title="This deal has no coordinates on file, so the map can't land on it. Draw the area from the canvas map instead.">
                    No coordinates
                  </span>
                ) : null}
              </>
            )}
          </span>
        );
      })}
      {unanchored.map((a) => <AreaSet key={a.area_group_id} area={a} />)}
      {msg && <span className="w-full text-[11px] text-slate-600">{msg}</span>}
    </div>
  );
}

function AreaSet({ area, label }: { area: AreaStats; label?: string }) {
  return (
    <a href={areaMapHref(area.area_group_id)} target="_blank" rel="noopener"
      className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-800 hover:bg-emerald-100"
      title={`${area.reps.map((r) => r.full_name).join(", ")} · ${area.pin_count} pins · open on the canvas map`}>
      {label ? `${label} · ${areaName(area)}` : <>✓ Area set · {areaName(area)}{area.reps.length > 1 && <span className="font-normal text-emerald-700">· with {area.reps.length - 1} more</span>}</>}
    </a>
  );
}

function dayWord(day: string): string {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  return day === today ? "today" : "that day";
}
